/**
 * Avisos efímeros.
 *
 * Toda acción de curaduría necesita confirmación visible: descartar un nodo lo
 * hace desaparecer de la vista, y sin un aviso el usuario no sabe si pasó algo
 * o si la app se colgó. Los errores no se autodescartan, porque perder el
 * mensaje de por qué falló algo es peor que un aviso que se queda.
 */

import { CheckCircle2, CircleAlert, Info, Undo2, X } from 'lucide-react'
import { useCallback, useMemo, useState, type ReactNode } from 'react'

import { ContextoAvisos, type ApiAvisos, type Aviso, type Tono } from './avisos-contexto'
import { cx } from './util'

const DURACION: Record<Tono, number> = {
  exito: 5000,
  info: 4000,
  // Los errores se quedan: el usuario tiene que poder leerlos y copiarlos.
  error: 0,
}

const ESTILO: Record<Tono, string> = {
  exito: 'border-[var(--color-favorable)]',
  error: 'border-[var(--color-opositora)]',
  info: 'border-[var(--color-borde-fuerte)]',
}

const ICONO: Record<Tono, ReactNode> = {
  exito: <CheckCircle2 size={15} className="text-[var(--color-favorable)]" />,
  error: <CircleAlert size={15} className="text-[var(--color-opositora)]" />,
  info: <Info size={15} className="text-[var(--color-texto-suave)]" />,
}

export function ProveedorAvisos({ children }: { children: ReactNode }) {
  const [avisos, setAvisos] = useState<Aviso[]>([])

  const quitar = useCallback((id: number) => {
    setAvisos((previos) => previos.filter((a) => a.id !== id))
  }, [])

  const agregar = useCallback(
    (aviso: Omit<Aviso, 'id'>) => {
      const id = Date.now() + Math.random()
      setAvisos((previos) => [...previos.slice(-4), { ...aviso, id }])
      const duracion = DURACION[aviso.tono]
      if (duracion > 0) setTimeout(() => quitar(id), duracion)
    },
    [quitar],
  )

  const api = useMemo<ApiAvisos>(
    () => ({
      exito: (texto, opciones) => agregar({ tono: 'exito', texto, ...opciones }),
      error: (texto, detalle) => agregar({ tono: 'error', texto, detalle }),
      info: (texto) => agregar({ tono: 'info', texto }),
    }),
    [agregar],
  )

  return (
    <ContextoAvisos.Provider value={api}>
      {children}
      <div
        className="pointer-events-none fixed right-3 bottom-3 z-50 flex w-[min(380px,calc(100vw-1.5rem))] flex-col gap-2"
        role="status"
        aria-live="polite"
      >
        {avisos.map((aviso) => (
          <div
            key={aviso.id}
            className={cx(
              'aparecer pointer-events-auto rounded-md border-l-4 bg-[var(--color-panel)] p-2.5 shadow-lg',
              'border border-[var(--color-borde)]',
              ESTILO[aviso.tono],
            )}
          >
            <div className="flex items-start gap-2">
              <span className="mt-px shrink-0">{ICONO[aviso.tono]}</span>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-medium">{aviso.texto}</p>
                {aviso.detalle && (
                  <p className="mt-0.5 text-[12px] break-words text-[var(--color-texto-suave)]">
                    {aviso.detalle}
                  </p>
                )}
                {aviso.deshacer && (
                  <button
                    onClick={() => {
                      aviso.deshacer!.fn()
                      quitar(aviso.id)
                    }}
                    className="mt-1.5 inline-flex items-center gap-1 text-[12px] font-medium text-[var(--color-acento)] hover:underline"
                  >
                    <Undo2 size={12} />
                    {aviso.deshacer.etiqueta}
                  </button>
                )}
              </div>
              <button
                onClick={() => quitar(aviso.id)}
                className="shrink-0 text-[var(--color-texto-tenue)] hover:text-[var(--color-texto)]"
                aria-label="Cerrar aviso"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </ContextoAvisos.Provider>
  )
}
